/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { getHttpContext } from "../error";
import { RETRY_POLICY } from "../types";
import type { HttpMethod, TRetryOptions } from "../types";

/**
 * Silent retry ladder: 1s and 3s ahead of INTERACTIVE's first 5s step.
 * INTERACTIVE gives up after three retries whatever their source, so a call
 * waits at most 1s + 3s + 5s ≈ 9s before its caller moves on.
 */
export const SILENT_RETRY_DELAYS_MS: ReadonlyArray<number> = [1000, 3000];

/**
 * The longest server-requested wait a silent call will sit through.
 *
 * `Retry-After` always sets the wait when a call does retry. But a silent
 * caller has a better option than waiting unseen — move on to its fallback
 * now. A request to wait longer than this is taken as "unavailable for now"
 * and ends the call instead.
 */
export const SILENT_MAX_RETRY_AFTER_MS: number = 10000;

/**
 * The delay a `Retry-After` header asks for, in ms, or null. Mirrors
 * RetryMechanism's reading of the header: delay-seconds or an HTTP-date.
 */
export function readRetryAfterMs(error: unknown): number | null {
  const context = getHttpContext(error);
  if (context === null) {
    return null;
  }
  const value: string | undefined = context.headers["retry-after"];
  if (value === undefined || value.trim().length === 0) {
    return null;
  }
  const seconds: number = Number(value);
  if (!Number.isNaN(seconds)) {
    return seconds * 1000;
  }
  const retryAt: number = Date.parse(value);
  return Number.isNaN(retryAt) ? null : retryAt - Date.now();
}

/**
 * Retry options for a call nobody is watching — a silent sign-in step, or a
 * background token refresh — that has a fallback to go to when it fails.
 *
 * INTERACTIVE with a short warm-up: no overlay, a bounded ≈ 9s, and
 * INTERACTIVE's own per-status rules, so a definitive 400 / 401 / 403 / 404 is
 * not retried. Ends at once when the server asks for a wait longer than
 * {@link SILENT_MAX_RETRY_AFTER_MS}.
 *
 * @param httpMethod - The call's method. INTERACTIVE only retries a 502 / 504
 *   for an idempotent method, so the DTS GETs pass "GET".
 * @param isTerminal - An extra caller rule for errors that must not be
 *   retried, checked before the `Retry-After` limit.
 */
export function createSilentRetryOptions(
  httpMethod?: HttpMethod,
  isTerminal?: (error: unknown) => boolean
): TRetryOptions {
  return {
    retryPolicy: RETRY_POLICY.INTERACTIVE,
    httpMethod: httpMethod,
    initialRetryDelaysMs: SILENT_RETRY_DELAYS_MS,
    isTerminal: (error: unknown): boolean => {
      if (isTerminal !== undefined && isTerminal(error)) {
        return true;
      }
      const retryAfterMs: number | null = readRetryAfterMs(error);
      return retryAfterMs !== null && retryAfterMs > SILENT_MAX_RETRY_AFTER_MS;
    }
  };
}
